import {Component, ViewChild, computed, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, take} from 'rxjs';
import {FormMode} from '../../../shared/types/common';
import {DrawerFormBase} from '../drawer-form-base';
import {ManagementStore} from '../management.store';
import {ManagementService} from '../management.service';
import {CustomValidators} from '../../../shared/validators';
import {objectArrayToSelectOptions} from '../../../utils/mappings';
import {sanitizeAndJoin} from '../../../utils/strings';
import {Brand, BrandPayload, BrandSummary, CheckDomainConflictsPayload, DomainConflict} from '../types';
import {DomainConflictModalComponent} from './domain-conflict-modal/domain-conflict-modal.component';
import {MetaPageFormGroup, MetaPagesComponent} from './meta-pages/meta-pages.component';
import {LinkedinTermFormGroup, LinkedinTermsComponent} from './linkedin-terms/linkedin-terms.component';
import {ResearchSectionComponent} from './research-section/research-section.component';
import {ResearchPromptComponent, ResearchPromptData} from './research-prompt/research-prompt.component';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {SearchBarComponent} from '../../../shared/components/search-bar/search-bar.component';
import {SearchResultListComponent} from '../../../shared/components/search-result-list/search-result-list.component';
import {ResultData} from '../../../shared/components/search-result-list/search-result-list.component.types';
import {FormChipListComponent} from '../../../shared/components/form-chip-list/form-chip-list.component';
import {TooltipIconComponent} from '../../../shared/components/tooltip-icon/tooltip-icon.component';
import {SelectItem, SelectMenuComponent, SelectMode} from '../../../shared/components/select-menu/select-menu.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {AssetVersionPipe} from '../../../pipes/asset-version.pipe';
import {tooltips} from '../../../utils/tooltips';

@Component({
  selector: 'app-brand-drawer',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TitledInputComponent,
    ButtonComponent,
    SearchBarComponent,
    SearchResultListComponent,
    FormChipListComponent,
    TooltipIconComponent,
    SelectMenuComponent,
    LoaderComponent,
    AssetVersionPipe,
    MetaPagesComponent,
    LinkedinTermsComponent,
    ResearchSectionComponent,
  ],
  templateUrl: './brand-drawer.component.html',
  styleUrl: './brand-drawer.component.scss',
})
export class BrandDrawerComponent extends DrawerFormBase<Brand> {
  public form: FormGroup<{
    brandKey: FormControl<string | null>;
    name: FormControl<string | null>;
    keywords: FormControl<string | null>;
    advertiserDomains: FormControl<ResultData[] | null>;
    manualAdvertiserDomain: FormControl<string | null>;
    clickUrlTerms: FormControl<ResultData[] | null>;
    manualClickUrlTerm: FormControl<string | null>;
    ocrTerms: FormControl<ResultData[] | null>;
    manualOcrTerm: FormControl<string | null>;
    logoPath: FormControl<string | null>;
    competingBrands: FormControl<string[] | null>;
    aiPresenceTopics: FormControl<string[] | null>;
    metaPages: FormArray<MetaPageFormGroup>;
    linkedinTerms: FormArray<LinkedinTermFormGroup>;
  }>;
  public readonly configs = {
    [FormMode.ADD]: {title: 'Add brand', submitText: 'Add', submit: (payload: BrandPayload) => this.addBrand(payload)},
    [FormMode.EDIT]: {title: 'Edit brand', submitText: 'Update', submit: (payload: BrandPayload) => this.editBrand(payload)},
  };

  public manualDomains: ResultData[] = [];
  public domainResults: ResultData[] = [];
  // Free-text match terms (DEV-583) — chip-backed like advertiser domains, but optional and manual-add only.
  public clickUrlTermResults: ResultData[] = [];
  public ocrTermResults: ResultData[] = [];
  public isSearching = false;
  public resultPlaceholder = ['Search for advertiser domains to add them to the list.', 'Search result will be limited to 1000 domains.'];
  public searchControl = new FormControl('');
  public redirectCallback = (result: ResultData) => window.open(`https://${result.value}`, '_blank', 'noopener');
  public logoLoading = false;
  public logoMessageState: {class?: string; message?: string} = {class: undefined, message: undefined};

  protected readonly SelectMode = SelectMode;
  protected readonly tooltips = tooltips.management.brandModal;

  // Competitor / topic options come from the store's cached summaries.
  protected readonly competitorOptions = computed<SelectItem[]>(() =>
    objectArrayToSelectOptions(this.store.brandSummaries(), 'brandKey', 'name').filter(item => item.key !== this.brandKey.value));
  protected readonly topicOptions = computed<SelectItem[]>(() =>
    this.store.aiTopics().map(topic => ({label: topic.name, key: topic.name})));

  private lastSearch: string | null | undefined;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(ManagementStore);
  private readonly api = inject(ManagementService);

  // Used to gate each social section into the payload only once it has loaded its existing rows (see
  // getBrandPayload) — so a save before the async load returns can't wipe the brand's saved rows.
  @ViewChild(MetaPagesComponent) private metaPagesComponent?: MetaPagesComponent;
  @ViewChild(LinkedinTermsComponent) private linkedinTermsComponent?: LinkedinTermsComponent;

  constructor() {
    super();
    this.store.ensureBrandSummaries();
    this.store.ensureAiTopics();
    this.form = this.buildForm();
    if (this.entity) {
      this.initEditMode();
    }
  }

  private buildForm() {
    return this.fb.group({
      brandKey: new FormControl({value: '', disabled: !!this.entity}, [CustomValidators.required('Brand key'), CustomValidators.brandKey('Brand key')]),
      name: new FormControl('', [CustomValidators.required('Name')]),
      keywords: new FormControl(''),
      advertiserDomains: new FormControl([] as ResultData[], [CustomValidators.minMaxElements(1)]),
      manualAdvertiserDomain: new FormControl('', [CustomValidators.url('Advertiser domain')]),
      // Terms are free text (e.g. `sana-jarritos / b0g54bpj8b`), NOT urls — so no url validator. The
      // lists themselves are optional: no minimum-count validator, unlike advertiserDomains.
      // Max lengths mirror the server DTOs (@MaxLength(512) click-URL / @MaxLength(255) OCR) so an
      // oversized term is rejected at add-time instead of failing the whole brand save with a 400.
      clickUrlTerms: new FormControl([] as ResultData[]),
      manualClickUrlTerm: new FormControl('', [CustomValidators.minLength(3, 'Click URL term'), CustomValidators.maxLength(512, 'Click URL term')]),
      ocrTerms: new FormControl([] as ResultData[]),
      // OCR terms are experimental (contains-matching can over-match creatives) — the UI is disabled
      // while the team evaluates. Existing terms still prefill and are preserved on save (payload
      // reads selectedOcrTerms, not this control).
      manualOcrTerm: new FormControl({value: '', disabled: true}, [CustomValidators.minLength(3, 'OCR term'), CustomValidators.maxLength(255, 'OCR term')]),
      logoPath: new FormControl(''),
      competingBrands: new FormControl<string[]>([]),
      aiPresenceTopics: new FormControl<string[]>([]),
      metaPages: this.fb.array<MetaPageFormGroup>([]),
      linkedinTerms: this.fb.array<LinkedinTermFormGroup>([]),
    });
  }

  private initEditMode(): void {
    this.currentMode = FormMode.EDIT;
    const entity = this.entity!;
    this.clickUrlTermResults = (entity.clickUrlTerms ?? []).map((term: string) => ({value: term, isSelected: true}));
    this.ocrTermResults = (entity.ocrTerms ?? []).map((term: string) => ({value: term, isSelected: true}));
    this.form.patchValue({
      ...entity,
      keywords: entity.keywords ? sanitizeAndJoin(entity.keywords) : '',
      advertiserDomains: [...this.selectedResults],
      competingBrands: entity.competingBrands.map((brand: BrandSummary) => brand.brandKey),
      aiPresenceTopics: entity.aiTopics ?? [],
      clickUrlTerms: [...this.selectedClickUrlTerms],
      ocrTerms: [...this.selectedOcrTerms],
    });
    this.domainResults = entity.advertiserDomains.map((domainString: string) => ({value: domainString, isSelected: true}));
  }

  get brandKey(): FormControl {
    return this.form.get('brandKey') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  get keywords(): FormControl {
    return this.form.get('keywords') as FormControl;
  }

  get advertiserDomains(): FormControl {
    return this.form.get('advertiserDomains') as FormControl;
  }

  get manualAdvertiserDomain(): FormControl {
    return this.form.get('manualAdvertiserDomain') as FormControl;
  }

  get logoPath(): FormControl {
    return this.form.get('logoPath') as FormControl;
  }

  get competingBrands(): FormControl<string[]> {
    return this.form.get('competingBrands') as FormControl<string[]>;
  }

  get aiPresenceTopics(): FormControl<string[]> {
    return this.form.get('aiPresenceTopics') as FormControl<string[]>;
  }

  get clickUrlTerms(): FormControl {
    return this.form.get('clickUrlTerms') as FormControl;
  }

  get manualClickUrlTerm(): FormControl {
    return this.form.get('manualClickUrlTerm') as FormControl;
  }

  get ocrTerms(): FormControl {
    return this.form.get('ocrTerms') as FormControl;
  }

  get manualOcrTerm(): FormControl {
    return this.form.get('manualOcrTerm') as FormControl;
  }

  get selectedClickUrlTerms(): ResultData[] {
    return this.clickUrlTermResults.filter(result => result.isSelected);
  }

  get selectedOcrTerms(): ResultData[] {
    return this.ocrTermResults.filter(result => result.isSelected);
  }

  get unselectedResults(): ResultData[] {
    return this.domainResults.filter(result => !result.isSelected);
  }

  get selectedResults(): ResultData[] {
    return [...this.domainResults, ...this.manualDomains].filter(result => result.isSelected);
  }

  get metaPages(): FormArray<MetaPageFormGroup> {
    return this.form.get('metaPages') as FormArray<MetaPageFormGroup>;
  }

  get linkedinTerms(): FormArray<LinkedinTermFormGroup> {
    return this.form.get('linkedinTerms') as FormArray<LinkedinTermFormGroup>;
  }

  submit(): void {
    // Block re-entry: form.pending does not make form.invalid true, so without this guard a second
    // click during validation/save would open a second statusChanges subscription (and a second save).
    if (this.saving()) {
      return;
    }
    // A just-typed Meta page id may still be validating. Wait for any in-flight async validation to
    // settle so a single click submits (or surfaces the error) instead of being consumed by it.
    if (this.form.pending) {
      this.saving.set(true);
      this.form.statusChanges
        .pipe(filter(status => status !== 'PENDING'), take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.runSubmit());
      return;
    }
    this.runSubmit();
  }

  private runSubmit(): void {
    if (this.form.invalid) {
      this.saving.set(false); // release so the user can fix and resubmit
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    const payload = this.getBrandPayload();
    const checkPayload: CheckDomainConflictsPayload = {
      domains: payload.advertiserDomains,
      ...(this.currentMode === FormMode.EDIT ? {brandKey: payload.brandKey} : {}),
    };

    this.api.checkDomainConflicts(checkPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (response.hasConflicts) {
            this.saving.set(false);
            this.showConflictModal(response.conflicts, payload);
          } else {
            this.executeSave(payload);
          }
        },
        error: () => {
          this.saving.set(false);
          this.utilService.openSnackBar('Failed to check domain conflicts', {variant: 'error', position: 'center'});
        },
      });
  }

  private showConflictModal(conflicts: DomainConflict[], payload: BrandPayload): void {
    const conflictDialogRef = this.dialog.open(DomainConflictModalComponent, {
      data: {conflicts, brandName: payload.name},
      panelClass: 'domain-conflict-modal',
      autoFocus: false,
    });
    conflictDialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.saving.set(true);
          this.executeSave(payload);
        }
      });
  }

  private executeSave(payload: BrandPayload): void {
    this.configs[this.currentMode].submit(payload);
  }

  private addBrand(payload: BrandPayload): void {
    this.store.addBrand(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.onSuccess(`Brand "${result.name}" added successfully`);
          // Offer research right away (DEV-522): a new brand has no social metrics until it is
          // researched, and the scheduled run is monthly. Only when there is something to research —
          // a source-less brand would just get a 400 from the trigger.
          if (this.hasResearchableSource(payload)) {
            this.dialog.open(ResearchPromptComponent, {
              data: {brandKey: payload.brandKey, brandName: result.name ?? payload.name} satisfies ResearchPromptData,
              width: '520px',
              autoFocus: false,
            });
          }
        },
        error: error => this.onError(error),
      });
  }

  private hasResearchableSource(payload: BrandPayload): boolean {
    return !!(payload.metaPages?.some(page => page.is_active) || payload.linkedinTerms?.some(term => term.is_active));
  }

  private editBrand(payload: BrandPayload): void {
    this.store.editBrand(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.onSuccess(`Brand "${result.name}" updated successfully`),
        error: error => this.onError(error),
      });
  }

  search(): void {
    // Guard before flipping isSearching: an empty/whitespace Enter used to set the flag with no
    // request to reset it, leaving the result-list spinner on and the Search button disabled.
    const term = this.searchControl.value?.trim();
    if (!term) {
      return;
    }
    this.isSearching = true;
    this.lastSearch = this.searchControl.value;
    this.api.searchAdvertisersDomains(term)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          const domains = result.domains.map(domain => ({value: domain, isSelected: false}));
          this.domainResults = this.selectedResults;
          const filteredDomains = domains.filter(dom1 => !this.domainResults.some(dom2 => dom2.value === dom1.value));
          if (filteredDomains.length === 0) {
            this.resultPlaceholder = ['No domains found 🥺', 'But here’s a unicorn: 🦄'];
          } else {
            this.domainResults.push(...filteredDomains);
          }
          this.isSearching = false;
        },
        error: () => {
          this.isSearching = false;
          this.utilService.openSnackBar('Failed to search advertiser domains', {variant: 'error', position: 'center'});
        },
      });
  }

  addManualAdvertiserDomain(): void {
    if (this.manualAdvertiserDomain.invalid) {
      return;
    }
    const domain = this.manualAdvertiserDomain.value.trim();
    if (domain) {
      this.manualDomains.push({value: domain, isSelected: true});
      this.manualAdvertiserDomain.reset();
    }
  }

  addManualClickUrlTerm(): void {
    this.addManualTerm(this.manualClickUrlTerm, this.clickUrlTermResults);
  }

  addManualOcrTerm(): void {
    this.addManualTerm(this.manualOcrTerm, this.ocrTermResults);
  }

  private addManualTerm(control: FormControl, results: ResultData[]): void {
    if (control.disabled || control.invalid) {
      return;
    }
    const term = control.value?.trim();
    if (!term) {
      return;
    }
    // Case-insensitive dedupe (the server lowercases terms anyway). Re-adding a removed chip just
    // re-selects the existing entry; a true duplicate is silently skipped — either way the input clears.
    const existing = results.find(result => result.value.toLowerCase() === term.toLowerCase());
    if (existing) {
      existing.isSelected = true;
    } else {
      results.push({value: term, isSelected: true});
    }
    control.reset();
  }

  copyAdvertiserDomains(): void {
    const snowflakeList = `(${this.selectedResults.map(result => `'${result.value}'`).join(',')})`;
    navigator.clipboard.writeText(snowflakeList)
      .then(() => this.utilService.openSnackBar('Copied to clipboard', {variant: 'success', position: 'center', duration: 2000}))
      .catch(() => this.utilService.openSnackBar('Failed to copy to clipboard', {variant: 'error', position: 'center'}));
  }

  getValidSearch(): boolean {
    const searchVal = this.searchControl.value as string;
    return searchVal.length > 2 && !this.isSearching && searchVal !== this.lastSearch;
  }

  getBrandPayload(): BrandPayload {
    const form = this.form.getRawValue();
    const payload = {
      brandKey: form.brandKey,
      name: form.name,
      competingBrands: form.competingBrands,
      keywords: this.keywords.value ? this.keywords.value.split(',') : [],
      advertiserDomains: this.selectedResults.map(domain => domain.value),
      logoPath: this.logoPath.value || undefined,
      aiPresenceTopics: form.aiPresenceTopics,
      // Always sent in full, like advertiserDomains (empty array = clear) — these live on the brand
      // itself, so they don't need the loaded()/dirty gating the async-loaded social sections use.
      clickUrlTerms: this.selectedClickUrlTerms.map(term => term.value),
      ocrTerms: this.selectedOcrTerms.map(term => term.value),
    } as BrandPayload;

    // Only serialize a social section once it has loaded its existing rows AND the user actually
    // changed it. Not loaded: a save would submit an empty list and the server reconciles by
    // replacement — wiping the brand's saved rows. Not dirty: omitting the untouched section lets the
    // server skip its Snowflake write entirely, keeping unrelated brand edits fast.
    if (this.metaPagesComponent?.loaded() && this.metaPages.dirty) {
      payload.metaPages = this.metaPages.controls
        .filter(row => row.get('pageId')?.value && (row.get('countries')?.value?.length ?? 0) > 0)
        .map(row => ({
          page_id: row.get('pageId')!.value as string,
          countries: row.get('countries')!.value as string[],
          is_active: row.get('isActive')!.value ?? true,
        }));
    }
    if (this.linkedinTermsComponent?.loaded() && this.linkedinTerms.dirty) {
      payload.linkedinTerms = this.linkedinTerms.controls
        .filter(row => row.get('searchTerm')?.value && (row.get('countries')?.value?.length ?? 0) > 0)
        .map(row => ({
          search_term: (row.get('searchTerm')!.value as string).trim(),
          countries: row.get('countries')!.value as string[],
          is_active: row.get('isActive')!.value ?? true,
        }));
    }
    return payload;
  }

  onLogoError(event: Event): void {
    // Through AssetVersionPipe like every other static asset, so the fallback gets cache-busted too.
    (event.target as HTMLImageElement).src = new AssetVersionPipe().transform('images/no-logo.png');
  }

  findLogo(): void {
    const brandName = this.name.value;
    if (!brandName || this.logoLoading) {
      return;
    }
    this.logoLoading = true;
    this.api.findBrandLogo(brandName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result?.logoURL) {
            this.logoPath.setValue(result.logoURL);
            this.logoMessageState = {class: 'success', message: 'Logo found. If incorrect, upload a custom one.'};
          } else {
            this.logoMessageState = {class: 'error', message: 'We couldn’t find a logo for this brand. Change name or upload a logo.'};
          }
          this.logoLoading = false;
        },
        error: () => {
          this.logoMessageState = {class: 'error', message: 'An error occurred. Please try again.'};
          this.logoLoading = false;
        },
      });
  }

  uploadLogo(): void {
    const brandName = this.name.value;
    if (!brandName || this.logoLoading) {
      return;
    }
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg, image/png';
    fileInput.onchange = () => {
      const files = fileInput.files;
      if (files && files.length > 0) {
        const logoFile = files[0];
        if (!this.isValidLogoFile(logoFile)) {
          return;
        }
        this.handleLogoUpload(brandName, logoFile);
      }
    };
    fileInput.click();
  }

  private isValidLogoFile(file: File): boolean {
    const maxSizeInMB = 2;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      this.logoMessageState = {class: 'error', message: `File size exceeds the maximum limit of ${maxSizeInMB} MB`};
      return false;
    }
    return true;
  }

  private handleLogoUpload(brandName: string, logoFile: File): void {
    this.logoLoading = true;
    this.api.uploadBrandLogo(brandName, logoFile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result?.logoURL) {
            this.logoPath.setValue(result.logoURL);
            this.logoMessageState = {class: 'success', message: 'Logo uploaded successfully.'};
          } else {
            this.logoMessageState = {class: 'error', message: 'An error occurred. Please try again.'};
          }
          this.logoLoading = false;
        },
        error: error => {
          if (error.status === 400) {
            this.logoMessageState = {class: 'error', message: error.error.message};
          } else {
            this.logoMessageState = {class: 'error', message: 'An error occurred. Please try again.'};
          }
          this.logoLoading = false;
        },
      });
  }
}
